import {
  MESSAGES_MOT_DE_PASSE,
  MOT_DE_PASSE_MIN,
  REGLES_MOT_DE_PASSE,
  messageRefusChangementMotDePasse,
  verifierChangementMotDePasse,
  type AuthenticatedUser,
  type ChangerMotDePasseRequest,
  type ChangerMotDePasseResponse,
  type SaisieChangementMotDePasse,
} from '@silex/shared';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';

import { ApiError, api } from '../api/client';
import { tokenStore } from '../api/tokens';
import { roleLabel, useAuth } from '../auth/AuthContext';

/**
 * « MON COMPTE » — AB#747.
 *
 * Fondateur, 15 octobre 2026 : un utilisateur connecté ne pouvait changer son
 * mot de passe NULLE PART. Cette page est ouverte à tout compte connecté de la
 * console (tuteur, admin) et porte trois choses : qui l'on est, l'état du
 * double facteur, et le changement de mot de passe.
 *
 * L'identité est relue au serveur (`GET /v1/auth/moi`) : la session reprise au
 * rechargement ne connaît ni le courriel ni l'état du double facteur — le jeton
 * ne les porte pas. En attendant la réponse, ce que la session sait.
 */
export function MonComptePage(): ReactNode {
  const { user } = useAuth();
  const [compte, setCompte] = useState<(AuthenticatedUser & { courriel: string }) | null>(null);
  const [lectureEchouee, setLectureEchouee] = useState(false);

  useEffect(() => {
    let annule = false;
    api
      .moi()
      .then((c) => {
        if (!annule) setCompte(c);
      })
      .catch(() => {
        if (!annule) setLectureEchouee(true);
      });
    return () => {
      annule = true;
    };
  }, []);

  const role = compte?.role ?? user?.role;
  return (
    <div className="content__inner">
      <header className="page-head">
        <div>
          <h1>Mon compte</h1>
          <p className="page-sub">
            Votre identité, votre mot de passe, votre double authentification.
          </p>
        </div>
      </header>

      <section className="card" aria-labelledby="compte-identite">
        <h3 id="compte-identite">Identité</h3>
        <dl>
          <dt>Adresse courriel</dt>
          <dd>{compte?.courriel ?? user?.email ?? '—'}</dd>
          <dt>Rôle</dt>
          <dd>{role ? roleLabel(role) : '—'}</dd>
        </dl>
        {lectureEchouee ? (
          <p className="form-note">L’état du compte n’a pas pu être relu pour le moment.</p>
        ) : null}
      </section>

      <section className="card" aria-labelledby="compte-mfa">
        <h3 id="compte-mfa">Double authentification</h3>
        <p data-testid="etat-mfa">{libelleMfa(compte)}</p>
      </section>

      <section className="card" aria-labelledby="compte-mdp">
        <h3 id="compte-mdp">Changer mon mot de passe</h3>
        <FormulaireMotDePasse appeler={api.changerMotDePasse} />
      </section>
    </div>
  );
}

/** L'état du double facteur, en une phrase. `null` : pas encore lu. */
export function libelleMfa(compte: Pick<AuthenticatedUser, 'mfaActif'> | null): string {
  if (!compte) return 'Lecture en cours…';
  return compte.mfaActif
    ? 'Activée : un code de votre application d’authentification est demandé à chaque connexion.'
    : 'Non activée sur ce compte.';
}

export type ResultatChangement =
  { readonly ok: true } | { readonly ok: false; readonly message: string };

/**
 * Le geste entier, hors du rendu pour être éprouvé sans navigateur : vérifie
 * la saisie, appelle l'API, et REMPLACE les jetons de la session — l'API a
 * révoqué toutes les sessions du compte, la courante comprise.
 */
export async function executerChangementMotDePasse(
  saisie: SaisieChangementMotDePasse,
  appeler: (input: ChangerMotDePasseRequest) => Promise<ChangerMotDePasseResponse>,
  poserJetons: (tokens: ChangerMotDePasseResponse['tokens']) => void = (tokens) => {
    tokenStore.setAccessToken(tokens.accessToken);
    tokenStore.setRefreshToken(tokens.refreshToken);
  },
): Promise<ResultatChangement> {
  const faute = verifierChangementMotDePasse(saisie);
  if (faute) return { ok: false, message: faute };
  try {
    const res = await appeler({
      motDePasseActuel: saisie.actuel,
      nouveauMotDePasse: saisie.nouveau,
    });
    poserJetons(res.tokens);
    return { ok: true };
  } catch (err) {
    return {
      ok: false,
      message: messageRefusChangementMotDePasse(err instanceof ApiError ? err : undefined),
    };
  }
}

export function FormulaireMotDePasse({
  appeler,
}: {
  appeler: (input: ChangerMotDePasseRequest) => Promise<ChangerMotDePasseResponse>;
}): ReactNode {
  const [actuel, setActuel] = useState('');
  const [nouveau, setNouveau] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [reussi, setReussi] = useState(false);
  const [enCours, setEnCours] = useState(false);

  async function soumettre(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setEnCours(true);
    setErreur(null);
    setReussi(false);
    const r = await executerChangementMotDePasse({ actuel, nouveau, confirmation }, appeler);
    setEnCours(false);
    if (r.ok) {
      setActuel('');
      setNouveau('');
      setConfirmation('');
      setReussi(true);
    } else {
      setErreur(r.message);
    }
  }

  return (
    <form onSubmit={soumettre} noValidate aria-label="Changer mon mot de passe">
      <ul className="form-note" aria-label="Règles du mot de passe">
        {REGLES_MOT_DE_PASSE.map((r) => (
          <li key={r}>{r}</li>
        ))}
      </ul>

      {erreur ? (
        <p className="form-error" role="alert">
          {erreur}
        </p>
      ) : null}
      {reussi ? (
        <p className="form-note" role="status">
          {MESSAGES_MOT_DE_PASSE.reussi}
        </p>
      ) : null}

      <div className="field">
        <label htmlFor="mdp-actuel">Mot de passe actuel</label>
        <input
          id="mdp-actuel"
          type="password"
          autoComplete="current-password"
          value={actuel}
          onChange={(e) => setActuel(e.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor="mdp-nouveau">Nouveau mot de passe</label>
        <input
          id="mdp-nouveau"
          type="password"
          autoComplete="new-password"
          minLength={MOT_DE_PASSE_MIN}
          value={nouveau}
          onChange={(e) => setNouveau(e.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor="mdp-confirmation">Confirmer le nouveau mot de passe</label>
        <input
          id="mdp-confirmation"
          type="password"
          autoComplete="new-password"
          value={confirmation}
          onChange={(e) => setConfirmation(e.target.value)}
        />
      </div>

      <button type="submit" className="btn" disabled={enCours}>
        {enCours ? 'Enregistrement…' : 'Changer mon mot de passe'}
      </button>
    </form>
  );
}
